import Control from '@/Controls/Control';
import { IGenericDTO } from "coreframetypings/dto";
import { IHeaderCol, ICoreFrameGrid } from 'rad-coreframe-grid';
import { Dictionary, ICoreFrameGridBindingParameter, IVerticalResizeOptions } from '#/DefaultViewExtensionInterfaces';
import Factory from '@/Factories/Factory';
import ComponentManager from '@/Management/ComponentManager';
import InlineEditingGridViewModel from '@/ViewModels/Controls/InlineEditingGridViewModel';
import IAsyncFactory from '@/Factories/IAsyncFactory';
import ResourceManager from '@/Management/ResourceManager';
import GridVerticalResizeManager from '@/Utility/GridVerticalResizeManager';

export default class InlineEditingGrid extends Control {

    private jQueryControl: JQuery;
    private originalWidths: Dictionary<string | number>;

    protected isNoEntriesElementVisible: KnockoutObservable<boolean>;

    private verticalResizeManager?: GridVerticalResizeManager;
    public defaultHeight: KnockoutObservable<string>;

    public constructor(protected viewModel: InlineEditingGridViewModel, protected element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
        this.originalWidths = {};
        this.defineSubscription(this.viewModel.dataSource, this.checkFittedColumns.bind(this));

        this.isNoEntriesElementVisible = this.createNoEntriesElementVisibleObs();
        this.verticalResizeManager = this.createVerticalResizeManager();
        this.defaultHeight = this.observableOrDefault(this.viewModel.defaultHeight, '100%');
    }

    protected createNoEntriesElementVisibleObs(): KnockoutObservable<boolean> {
        return this.defineComputed(() => {
            if (!this.viewModel.showNoEntriesLabel)
                return false;

            const isBusy = this.viewModel.isBusy();
            const hasNoData = this.viewModel.dataSource().length === 0;
            return !isBusy && hasNoData;
        });
    }
    
    protected getCoreFrameGrid(): ICoreFrameGrid {
        return this.jQueryControl.data('coreframegrid') as ICoreFrameGrid;
    }

    public checkFittedColumns(): void {
        if (this.isDisposed)
            return;

        const grid = this.getCoreFrameGrid();
            if (!grid)
                return;
            
        grid.checkFittedColumns();

        // Some factories load their data asynchronously. When their data is fully loaded, 
        // we need to check again whether or not the content fits in the columns.
        const debouncedCheckFittedColumns = _.debounce(() => grid.checkFittedColumns(), 100);
        const factories = this.viewModel.getEditContexts()
            .map(c => c.getCreatedFactories())
            .reduce((a, b) => a.concat(b), [])
            .filter(f => this.isAsyncFactory(f))
            .map(f => f as IAsyncFactory)
            .filter(f => !f.isDisposed && !f.isFullyLoaded())
            .slice(0, 100); // Limiting the amount of relevant factories to narrow a possible performance impact
        for (const factory of factories) {
            const subscription = factory.isFullyLoaded.subscribe(loaded => {
                if (loaded) {
                    // Debouncing serves two purposes here: 
                    // 1) Having a slight delay after changing the factories data means it should then be rendered completely
                    // 2) Not checking too often when having many factories that got updated
                    debouncedCheckFittedColumns();

                    // Once the factory is fully loaded, the content won't change anymore and we don't need to check again.
                    subscription.dispose();
                }
            });
            this.subscriptionCollection.push(subscription);
        }
    }

    private isAsyncFactory(obj: Factory): obj is IAsyncFactory {
        return 'isFullyLoaded' in obj;
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div></div>');

        this.applyGridBindings();
        this.setupNoEntriesElement();
        
        return this.jQueryControl;
    }

    protected applyGridBindings() {
        const gridParameters = this.getGridBindingParameter();
        this.applyBindings(this.jQueryControl, {
            coreFrameGrid: gridParameters
        });
    }

    private setupNoEntriesElement() {
        if (!this.viewModel.showNoEntriesLabel)
            return;

        const $gridBody = this.jQueryControl.find('.GridBodyTable');
        if (!$gridBody.length)
            return;

        const $noEntriesElement = $('<div>').addClass('no-entries');
        this.applyBindings($noEntriesElement, {
            text: ResourceManager.getInstance().get('NO_ENTRIES'),
            visible: this.isNoEntriesElementVisible,
        });
        $noEntriesElement.insertBefore($gridBody);
    }

    private onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'inlineeditinggrid',
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            visible: this.viewModel.isVisible,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            attr: {
                "data-view": this.identifier
            },

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });
    }

    /**
     * Before-Editing callback
     * @param row
     */
    private onBeforeEdit(row: IGenericDTO): JQueryPromise<boolean> {
        this.setColumnSizesForEditMode();
        return this.viewModel.onBeforeEdit(row);
    }

    /**
     * Save callback
     * @param row
     */
    private onSave(row: IGenericDTO): JQueryPromise<boolean> {
        return this.viewModel.onSave(row)
            .then((success) => {
                if (success)
                    this.resetColumnSizes();

                return success;
            });
    }

    /**
     * Create callback
     * @param row
     */ 
    private onAdd(row: IGenericDTO): JQueryPromise<boolean | IGenericDTO> {
        return this.viewModel.onAdd()
            .then((success) => {
                if (success !== false) {
                    this.setColumnSizesForEditMode();
                }

                return success;
            });
    }

    /**
     * Delete callback
     * @param row
     */
    private onDelete(row: IGenericDTO): JQueryPromise<boolean> {
        return this.viewModel.onDelete(row)
            .then((success) => {
                if (success)
                    this.resetColumnSizes();

                return success;
            });
    }

    protected getColumnsForResizing(): IHeaderCol[] {
        return this.viewModel.columns().slice(1);
    }

    /**
     * Sets colum sizes for edit mode
     */
    protected setColumnSizesForEditMode(): void {
        if (this.isDisposed)
            return;

        if (this.viewModel.dataSource().length == 0)
            return;

        const grid = this.getCoreFrameGrid();
        if (!grid)
            return;
        
        const columns = this.getColumnsForResizing();
        const widthForEditMode = 200;

        if (this.needCustomMinColWidth()) {
            (grid as any).options.colMinWidth = 90; // TODO: options ist ein privates property...
        }

        columns.forEach((column) => {
            if (!column.isEditable || parseInt(column.width as any) > widthForEditMode)
                return;
            this.originalWidths[column.name] = column.width;
            grid.setColumnWidth(column.name, widthForEditMode);
        });
    }

    /**
     * Resets column sizes to original size after leaving edit mode
     */
    protected resetColumnSizes(): void {
        if (this.isDisposed || _.keys(this.originalWidths).length == 0)
            return;

        const grid = this.getCoreFrameGrid();
        if (!grid)
            return;
        
        const columns = this.getColumnsForResizing();

        if (this.needCustomMinColWidth()) {
            (grid as any).options.colMinWidth = 50; // TODO: options ist ein privates property...
        }

        columns.forEach((column) => {
            const width = this.originalWidths[column.name];
            grid.setColumnWidth(column.name, width);
        });

        this.originalWidths = {};
    }

    protected needCustomMinColWidth(): boolean {
        return true;
    }

    protected getGridBindingParameter(): Partial<ICoreFrameGridBindingParameter> {
        const gridParams: Partial<ICoreFrameGridBindingParameter> = {
            columns: this.viewModel.columns,
            onColumnResized: this.viewModel.onColumnResized.bind(this.viewModel),
            data: this.viewModel.dataSource,
            rowIdName: this.viewModel.rowIdName,
            sortable: this.definePureComputed({
                read: () => {
                    return {
                        mode: this.viewModel.isMultiColumnSort() ? 'multiple' : 'single',
                        allowUnsort: false
                    };
                }
            }),
            height: this.viewModel.height,
            editable: this.viewModel.isEditable,
            enabled: this.viewModel.isEnabled,
            editMode: this.viewModel.editMode,
            doubleClickToEdit: true,
            applyOnLostFocus: true,
            onSortChange: this.viewModel.onSortChange.bind(this.viewModel),
            onBeforeEdit: this.onBeforeEdit.bind(this),
            onSave: this.onSave.bind(this),
            onDelete: this.onDelete.bind(this),
            canAdd: this.viewModel.canAdd,
            fireEditWithoutClick: this.viewModel.fireEditWithoutClick,
            onAdd: this.onAdd.bind(this),
            widget: this.widget,
            locator: this.viewModel.locator,
            onFocusLost: (event: JQuery.MouseUpEvent) => {
                let $target = $(event.target)
                // Kendo-Popups (e.g. dropdowns) are ignored
                if ($target.parents('.k-animation-container, .k-window').length > 0) {
                    event.stopPropagation();
                }
            },
            onHeightReset: () => {
                this.verticalResizeManager?.resetUserStorageAndTriggerRecalculation();
            },
            renderGridAsync: false,
            defaultHeight: this.defaultHeight
        }

        this.verticalResizeManager?.manage(gridParams);

        return gridParams;
    }

    protected createVerticalResizeManager(): GridVerticalResizeManager {
        const verticalResize = this.viewModel.verticalResize;
        if (!verticalResize)
            return undefined;

        const verticalResizeManager = new GridVerticalResizeManager(verticalResize);

        // Wenn sich die Daten des Grids aktualisieren, soll die Höhenberechnung angestoßen werden
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.dataSource, () => false);

        // Da sowohl isNoEntriesElementVisible als auch die automatische Höhenberechnung von der Anzahl der Zeilen
        // abhängig sind, kann es vorkommen, dass die Höhenberechnung zuerst (einen Tick vor isNoEntriesElementVisible)
        // ausgelöst wird. In diesem Fall würde das "no-entries"-Element in die Höhenberechnung einfließen und eine
        // falsche Höhe berechnet werden. Deshalb lassen wir die automatische Höhenberechnung erneut auslösen, wenn
        // sich isNoEntriesElementVisible ändert.
        verticalResizeManager.registerAutoHeightDependency(this.isNoEntriesElementVisible, () => false);

        // Wenn sich die Sichtbarkeit ändert (z.B. durch einen umgebenden Tab), kann sich auch die Darstellung ändern.
        // Wenn das Grid nicht dargestellt wird, kann die Berechnung abgebrochen werden.
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.isDisplayed, displayed => !displayed);

        // Solange das Grid noch am Rendern ist, muss keine Höhenberechnung angestoßen werden.
        // Dies soll erst geschehen, wenn das Grid fertig gerendert ist.
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.isBusy, busy => busy);

        return verticalResizeManager;
    }
}

ComponentManager.setControl('InlineEditingGrid', InlineEditingGrid, 'RADPresentation.HTMLClient.DefaultViewExtensions');